import { useCallback, useMemo } from 'react';
import { useGetPromoCalendarQuery } from '../../../../redux/api/fymAPI';
import { useRealtimePollInterval } from '../../../realtime/useRealtimePollInterval';
import { CalendarView, dayKey, parseDayKey, viewDays, viewRange } from './calendarDates';

/** The poll interval of the Calendar while the realtime socket is down. */
export const CALENDAR_POLL_MS = 60_000;

interface UseCalendarRangeProps {
  accountId: number;
  view: CalendarView;
  anchor: Date;
}

/**
 * The days of the view and the Calendar read of their range: the posts with their songs, the
 * songs and the rollout days (`GET promo/calendar`). The realtime events keep it fresh; it polls
 * only while the socket is down (`useRealtimePollInterval`).
 */
export function useCalendarRange({ accountId, view, anchor }: UseCalendarRangeProps) {
  // The day of today changes at midnight; the key keeps the days stable during a day.
  const todayKey = dayKey(new Date());
  const days = useMemo(() => viewDays(view, anchor, parseDayKey(todayKey) ?? new Date()), [view, anchor, todayKey]);
  const { from, to } = useMemo(() => viewRange(days), [days]);
  const pollingInterval = useRealtimePollInterval(CALENDAR_POLL_MS);
  const { data, isLoading, isFetching, error, refetch } = useGetPromoCalendarQuery(
    { accountId, from, to },
    { pollingInterval }
  );

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  return { days, from, to, data, isLoading, isFetching, error, retry };
}
